import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Package, Play, Server, ShoppingCart, Gauge, BadgeCheck, Box } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import SiteNav from "@/components/SiteNav";
import MarketTabs from "@/components/MarketTabs";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import { ENGINES, MODDING_ENGINES, CLEARANCE_COLORS } from "@/content/engines";

const EASE = [0.16, 1, 0.3, 1];
const DIFF_CLS = {
  Easy: "text-nc-green border-nc-green/40 bg-nc-green/10",
  Moderate: "text-nc-blue border-nc-blue/40 bg-nc-blue/10",
  Advanced: "text-nc-orange border-nc-orange/40 bg-nc-orange/10",
  Expert: "text-red-400 border-red-500/40 bg-red-500/10",
};
const COMP_CLS = {
  pass: "text-nc-green border-nc-green/40 bg-nc-green/10",
  flagged: "text-red-400 border-red-500/40 bg-red-500/10",
  unverified: "text-zinc-400 border-white/15 bg-white/5",
};
const AI_PACK_ENGINE = { icon: Box, accent: "text-nc-orange border-nc-orange/60 bg-nc-orange/10", short: "AI", label: "AI Packs" };

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { fmt } = useCurrency();
  const [tab, setTab] = useState("");
  const [listings, setListings] = useState([]);
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem("nc_user")); } catch { return null; } });

  const load = useCallback(() => {
    api.get("/market/listings", { params: tab ? { engine: tab } : {} }).then((r) => setListings(r.data)).catch(() => {});
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const acquire = async (l) => {
    if (!user) { navigate("/auth"); return; }
    try {
      const r = await api.post(`/market/listings/${l.id}/acquire`);
      toast.success("Acquisition order created — fund escrow to proceed");
      navigate(`/command?engagement=${r.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="marketplace-page" className="min-h-screen bg-[#050505] text-zinc-100 selection:bg-nc-blue/30">
      <Seo title="Game Modding Marketplace — FiveM (GTA V), Arma Reforger, Minecraft" description="Buy verified mods, scripts and resources for FiveM (GTA V), Arma Reforger and Minecraft from clearance-rated developers. Framework compatibility, difficulty ratings and secure source handoff." path="/marketplace" keywords="fivem scripts for sale, arma reforger addons, minecraft plugins marketplace, gta v mods, fivem mlo, qb-core scripts, game mod marketplace" />
      <SiteNav />
      <MarketTabs />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
          <p className="font-mono text-[9px] tracking-[0.35em] text-nc-orange uppercase">Verified Mods & Resources // Specialist Contractors</p>
          <h1 className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-4xl text-zinc-50 mt-2 flex items-center gap-3">
            <Package className="w-8 h-8 text-nc-orange" /> Modding Marketplace
          </h1>
          <p className="font-mono text-[11px] text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            Production-ready mods and resources from verified Specialist Contractors. Source files transfer only after admin-approved fund release — Secure Handoff Protocol.
          </p>
        </motion.div>

        {/* Category tabs */}
        <div className="flex gap-2 flex-wrap mt-8">
          <button data-testid="market-tab-all" onClick={() => setTab("")} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${tab === "" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
            All Divisions
          </button>
          {MODDING_ENGINES.map((e) => {
            const Icon = ENGINES[e].icon;
            return (
              <button key={e} data-testid={`market-tab-${e}`} onClick={() => setTab(e)} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors flex items-center gap-2 ${tab === e ? ENGINES[e].accent : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
                <Icon className="w-3.5 h-3.5" /> {ENGINES[e].label}
              </button>
            );
          })}
          <button data-testid="market-tab-ai-packs" onClick={() => setTab("AI Packs")} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors flex items-center gap-2 ${tab === "AI Packs" ? AI_PACK_ENGINE.accent : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
            <Box className="w-3.5 h-3.5" /> AI Packs
          </button>
        </div>

        {/* Listings */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
          {listings.length === 0 && (
            <p data-testid="listings-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase col-span-full py-10 text-center">
              No listings in this category yet — Specialist Contractors can publish from the Dev Hub.
            </p>
          )}
          {listings.map((l, i) => {
            const eng = ENGINES[l.engine] || AI_PACK_ENGINE;
            const Icon = eng.icon;
            const cl = l.seller_clearance || { level: "L5" };
            return (
              <motion.div
                key={l.id}
                data-testid={`listing-card-${i + 1}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05, ease: EASE }}
                className="border border-white/10 bg-black/40 hover:border-nc-orange/40 transition-colors duration-300 flex flex-col"
              >
                <div className="border-b border-white/10 px-4 py-2 flex items-center justify-between gap-2">
                  <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-0.5 flex items-center gap-1.5 shrink-0 ${eng.accent}`}><Icon className="w-3 h-3" /> {eng.short}</span>
                  {l.game_title && <span data-testid={`listing-game-${i + 1}`} className="font-mono text-[8px] tracking-[0.2em] uppercase text-fuchsia-300 truncate">{l.game_title}</span>}
                  <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-0.5 shrink-0 ${COMP_CLS[l.compliance]}`}>
                    {l.compliance === "pass" ? "Compliance OK" : l.compliance === "flagged" ? "Flagged" : "Unverified"}
                  </span>
                </div>
                {l.ai_pack_id && l.cover_asset_id && (
                  <div data-testid={`listing-cover-${i + 1}`} className="border-b border-white/10 relative">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/creators/packs/${l.ai_pack_id}/cover`} alt={l.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                    <span className="absolute top-2 left-2 font-mono text-[8px] tracking-[0.2em] uppercase bg-black/80 border border-nc-orange/50 text-nc-orange px-2 py-0.5">AI Generated Pack</span>
                  </div>
                )}
                {!l.ai_pack_id && l.has_cover && (
                  <div data-testid={`listing-cover-${i + 1}`} className="border-b border-white/10">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/market/listings/${l.id}/cover`} alt={l.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-sans font-bold text-base text-zinc-50">{l.title}</p>
                    <p className="font-sans font-bold text-lg text-nc-green shrink-0">{fmt(l.price, 0)}</p>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-400 mt-1.5 leading-relaxed line-clamp-3">{l.description}</p>
                  <div className="flex gap-1.5 flex-wrap mt-3">
                    {l.frameworks.map((f) => (
                      <span key={f} className="font-mono text-[8px] tracking-[0.15em] uppercase border border-nc-orange/40 text-nc-orange px-2 py-0.5">{f}</span>
                    ))}
                    <span className={`font-mono text-[8px] tracking-[0.15em] uppercase border px-2 py-0.5 flex items-center gap-1 ${DIFF_CLS[l.difficulty]}`}><Gauge className="w-3 h-3" /> {l.difficulty}</span>
                  </div>
                  {/* Compatibility matrix */}
                  <div className="mt-3 border border-white/10 bg-[#050505]/60 px-3 py-2">
                    <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase mb-1.5">Compatibility Matrix</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {l.compat_versions.map((v) => (
                        <span key={v} className="font-mono text-[8px] text-nc-blue border border-nc-blue/30 px-1.5 py-0.5">{eng.short} {v}</span>
                      ))}
                      {l.compat_versions.length === 0 && <span className="font-mono text-[8px] text-zinc-600">All current builds</span>}
                    </div>
                  </div>
                  {(l.video_url || l.test_server) && (
                    <div className="flex gap-2 mt-3">
                      {l.video_url && (
                        <a data-testid={`listing-video-${i + 1}`} href={l.video_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[8px] tracking-[0.15em] uppercase border border-white/15 text-zinc-300 hover:border-nc-blue hover:text-nc-blue px-2.5 py-1.5 flex items-center gap-1.5 transition-colors"><Play className="w-3 h-3" /> Video Walkthrough</a>
                      )}
                      {l.test_server && (
                        <a data-testid={`listing-testserver-${i + 1}`} href={l.test_server} target="_blank" rel="noopener noreferrer" className="font-mono text-[8px] tracking-[0.15em] uppercase border border-white/15 text-zinc-300 hover:border-nc-green hover:text-nc-green px-2.5 py-1.5 flex items-center gap-1.5 transition-colors"><Server className="w-3 h-3" /> Join Test Server</a>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                    <p className="font-mono text-[9px] text-zinc-500 flex items-center gap-1.5">
                      <BadgeCheck className="w-3 h-3 text-nc-green" /> {l.seller_name}
                      <span className={`border px-1.5 py-0.5 ${CLEARANCE_COLORS[cl.level]}`}>L{cl.level}</span>
                    </p>
                    <button data-testid={`acquire-${i + 1}`} onClick={() => acquire(l)} className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.15em] text-[9px] px-3.5 py-2 flex items-center gap-1.5 transition-colors">
                      <ShoppingCart className="w-3.5 h-3.5" /> Acquire
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
